import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site';

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = keyword.trim();
    navigate(trimmed ? `/ranking?q=${encodeURIComponent(trimmed)}` : '/ranking');
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-200 ${
        scrolled ? 'border-background-300/70 bg-background-50/95 backdrop-blur' : 'border-transparent bg-background-50'
      }`}
    >
      <div className="hidden border-b border-background-200 bg-background-100 px-4 py-1.5 text-xs text-foreground-600 md:px-6 lg:flex lg:items-center lg:justify-between">
        <span className="flex items-center gap-2">
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary-500 animate-softPulse" />
          实时播报中 · 榜单每 15 分钟刷新一次
        </span>
        <span className="flex items-center gap-4">
          <span>爆料热线邮箱：{siteInfo.email}</span>
          <Link to="/contact" className="cursor-pointer text-primary-700 hover:text-primary-600">
            我要投稿爆料
          </Link>
        </span>
      </div>

      <div className="flex w-full items-center gap-4 px-4 py-3 md:px-6">
        <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-lg font-bold text-background-50">
            爆
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold text-foreground-950">{siteInfo.name}</span>
            <span className="mt-0.5 text-[11px] tracking-wide text-foreground-500">{siteInfo.tagline}</span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex" aria-label="主导航">
          {siteInfo.nav.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                isActive(item.path)
                  ? 'bg-primary-100 font-medium text-primary-800'
                  : 'text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="ml-auto hidden items-center md:flex">
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5">
            <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="search"
              placeholder="搜索话题 / 事件"
              aria-label="搜索话题"
              className="w-36 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-500 lg:w-44"
            />
          </div>
        </form>

        <Link
          to="/breaking"
          className="ml-auto hidden shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:ml-0 md:flex"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-flashlight-fill text-sm" />
          </span>
          突发速递
        </Link>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="打开导航菜单"
          aria-expanded={mobileOpen}
          className="ml-auto flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </span>
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 lg:hidden">
          <form onSubmit={handleSearch} className="mb-3">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="search"
                placeholder="搜索话题 / 事件"
                aria-label="搜索话题"
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-500"
              />
            </div>
          </form>
          <div className="flex flex-col gap-1">
            {siteInfo.nav.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`cursor-pointer rounded-md px-3 py-2.5 text-sm ${
                  isActive(item.path) ? 'bg-primary-100 font-medium text-primary-800' : 'text-foreground-700'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 border-t border-background-200 pt-3">
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}